'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { soinCategories } from '@/app/lib/soins';
import { atelierCategories } from '@/app/lib/ateliers';

interface DropdownColumn {
  title: string;
  items: { label: string; href: string }[];
}

interface NavLink {
  label: string;
  href: string;
  columns?: DropdownColumn[];
}

const soinsColumns: DropdownColumn[] = soinCategories.map((category) => ({
  title: category.name,
  items: category.soins.map((soin) => ({
    label: soin.name,
    href: `/soins/${soin.slug}`,
  })),
}));

const ateliersColumns: DropdownColumn[] = atelierCategories.map((category) => ({
  title: category.name,
  items:
    category.ateliers.length > 0
      ? category.ateliers.map((atelier) => ({
          label: atelier.name,
          href: `/ateliers-collectifs/${atelier.slug}`,
        }))
      : [{ label: 'Programme à venir', href: `/ateliers-collectifs#${category.slug}` }],
}));

const navLinks: NavLink[] = [
  { label: 'Soins', href: '/#services', columns: soinsColumns },
  { label: 'Ateliers collectifs', href: '/ateliers-collectifs', columns: ateliersColumns },
  { label: 'Réservation', href: '/#booking' },
  // { label: 'À propos', href: '/#about' }, // section "À propos" pas encore prête
  { label: 'Infos', href: '/#info' },
  { label: 'Contact', href: '/#contact' },
];

function ChevronIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 12" fill="none" className={className}>
      <path
        d="M2.5 4.5L6 8l3.5-3.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState<string | null>(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setOpenMobileSubmenu(null);
  };

  return (
    <nav
      className={`sticky top-0 z-40 bg-background-primary transition-all duration-500 ${
        scrolled ? 'shadow-sm border-b border-cream-300/40' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-8">
        {/* Logo */}
        <Link href="/#hero" className="shrink-0">
          <Image
            src="/logo.png"
            alt="La Canopée de Christelle"
            width={684}
            height={228}
            className="h-11 w-auto"
            priority
          />
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map((l) => (
            <div key={l.label} className="relative group">
              <Link
                href={l.href}
                className="flex items-center gap-1 font-sans text-sm text-forest-dark hover:text-forest tracking-wide transition-colors py-2"
              >
                {l.label}
                {l.columns && (
                  <ChevronIcon className="w-3 h-3 mt-px transition-transform duration-200 group-hover:rotate-180" />
                )}
              </Link>

              {l.columns && (
                <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50">
                  <div className="flex gap-8 bg-cream-50/98 backdrop-blur-md border border-cream-300/40 rounded-2xl shadow-lg p-6">
                    {l.columns.map((col) => (
                      <div key={col.title} className="flex flex-col gap-2 min-w-[190px]">
                        <p className="font-sans text-xs tracking-[0.2em] uppercase text-forest-light mb-1">
                          {col.title}
                        </p>
                        {col.items.map((item) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            className="font-sans text-sm text-forest-dark/80 hover:text-forest whitespace-nowrap transition-colors py-1"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          href="/#booking"
          className="hidden md:inline-flex items-center bg-forest text-cream-100 font-sans text-sm px-5 py-2.5 rounded-full hover:bg-forest-dark transition-colors tracking-wide shrink-0"
        >
          Réserver
        </Link>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden flex flex-col justify-center gap-1.5 w-8 h-8 cursor-pointer"
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          <span
            className={`block h-px w-6 bg-forest-dark transition-all duration-300 origin-center ${
              menuOpen ? 'rotate-45 translate-y-[7px]' : ''
            }`}
          />
          <span
            className={`block h-px w-6 bg-forest-dark transition-all duration-300 ${
              menuOpen ? 'opacity-0 scale-x-0' : ''
            }`}
          />
          <span
            className={`block h-px w-6 bg-forest-dark transition-all duration-300 origin-center ${
              menuOpen ? '-rotate-45 -translate-y-[7px]' : ''
            }`}
          />
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-[80vh] overflow-y-auto' : 'max-h-0'
        }`}
      >
        <div className="bg-background-primary border-t border-cream-300/40 px-6 py-5 flex flex-col gap-1">
          {navLinks.map((l) => (
            <div key={l.label} className="border-b border-cream-200/60 last:border-b-0">
              <div className="flex items-center justify-between">
                <Link
                  href={l.href}
                  onClick={closeMobileMenu}
                  className="flex-1 font-sans text-forest-dark hover:text-forest transition-colors py-3"
                >
                  {l.label}
                </Link>
                {l.columns && (
                  <button
                    onClick={() =>
                      setOpenMobileSubmenu(openMobileSubmenu === l.label ? null : l.label)
                    }
                    aria-label={`Afficher les sous-catégories de ${l.label}`}
                    className="p-3 text-forest-dark"
                  >
                    <ChevronIcon
                      className={`w-3 h-3 transition-transform duration-200 ${
                        openMobileSubmenu === l.label ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                )}
              </div>

              {l.columns && (
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openMobileSubmenu === l.label ? 'max-h-[600px] pb-4' : 'max-h-0'
                  }`}
                >
                  <div className="flex flex-col gap-4 pl-3">
                    {l.columns.map((col) => (
                      <div key={col.title} className="flex flex-col gap-1.5">
                        <p className="font-sans text-xs tracking-[0.2em] uppercase text-forest-light">
                          {col.title}
                        </p>
                        {col.items.map((item) => (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={closeMobileMenu}
                            className="font-sans text-sm text-forest-dark/80 hover:text-forest transition-colors py-1"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Link
            href="/#booking"
            onClick={closeMobileMenu}
            className="bg-forest text-cream-100 font-sans text-center px-5 py-3 rounded-full hover:bg-forest-dark transition-colors mt-3"
          >
            Réserver
          </Link>
        </div>
      </div>
    </nav>
  );
}
